// StepHero.tsx — the typed hero header for non-video steps on the home screen.
//
// Video lessons keep the cinematic dark Plate. Every other step type gets a
// light, paper-world panel keyed to the brand palette: an accent tint, a quiet
// motif, and a glyph "medallion". When a lesson has an illustration the card
// turns WHITE (the transparent PNGs are drawn for white). Full-bleed images
// (lessons whose whole content is the image) fill the card clean — no dark mask;
// the title sits in the footer below (like video tiles) so nothing covers them.
import { GlyphWell, StatePill, type PillState } from './ui';
import { Icon, type IconName } from './icons';
import { FavouriteButton } from './FavouriteButton';
import type { StepType } from '@/types';

type Accent = 'amber' | 'green';
type Motif = 'rule' | 'ripple' | 'rings' | 'wave' | 'none';

interface Theme {
  accent: Accent;
  motif: Motif;
  glyph: IconName;
  label: string;
}

const THEME: Record<Exclude<StepType, 'video'>, Theme> = {
  article: { accent: 'amber', motif: 'rule', glyph: 'book', label: 'Reading' },
  reflection: { accent: 'green', motif: 'ripple', glyph: 'pencil', label: 'Journalling' },
  exercise: { accent: 'green', motif: 'rings', glyph: 'leaf', label: 'Exercise' },
  audio: { accent: 'amber', motif: 'wave', glyph: 'audio', label: 'Audio' },
  mixed: { accent: 'amber', motif: 'none', glyph: 'sparkle', label: 'Lesson' },
};
const AHA_THEME: Theme = { accent: 'amber', motif: 'rings', glyph: 'sparkle', label: 'Aha moment' };

const ACCENT_RGB: Record<Accent, string> = { amber: '200,134,26', green: '29,158,117' };
const ACCENT_DEEP: Record<Accent, string> = { amber: 'var(--amber-deep)', green: 'var(--green-deep)' };
const FIELD: Record<Accent, string> = {
  amber: 'linear-gradient(155deg, #fdf4e4 0%, #f7e7c8 100%)',
  green: 'linear-gradient(155deg, #f4f3e8 0%, #e7efe7 100%)',
};

export function stepTheme(type: StepType): Theme | null {
  if (type === 'video') return null;
  return THEME[type] ?? THEME.mixed;
}

function Motif({ kind, rgb }: { kind: Motif; rgb: string }) {
  const c = (a: number) => `rgba(${rgb},${a})`;
  if (kind === 'none') return null;
  if (kind === 'wave') {
    return (
      <svg viewBox="0 0 120 24" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: '46%', opacity: 0.5 }}>
        <path d="M0 12 Q 10 2 20 12 T 40 12 T 60 12 T 80 12 T 100 12 T 120 12" fill="none" stroke={c(0.22)} strokeWidth="1.4" />
        <path d="M0 17 Q 10 9 20 17 T 40 17 T 60 17 T 80 17 T 100 17 T 120 17" fill="none" stroke={c(0.12)} strokeWidth="1.2" />
      </svg>
    );
  }
  const bg =
    kind === 'rule'
      ? `repeating-linear-gradient(to bottom, transparent 0, transparent 21px, ${c(0.1)} 21px, ${c(0.1)} 22px)`
      : kind === 'ripple'
        ? `repeating-radial-gradient(circle at 50% 44%, transparent 0, transparent 19px, ${c(0.09)} 19px, ${c(0.09)} 20px)`
        : `repeating-radial-gradient(circle at 50% 44%, transparent 0, transparent 30px, ${c(0.08)} 30px, ${c(0.08)} 32px)`;
  return <div aria-hidden="true" style={{ position: 'absolute', inset: 0, backgroundImage: bg }} />;
}

export function StepHero({
  type,
  stepId,
  metaText,
  aha = false,
  image,
  imageFull = false,
  pill = 'current',
  ratio = '16 / 8.4',
}: {
  type: StepType;
  stepId: string;
  metaText?: string;
  aha?: boolean;
  image?: string;
  /** The image is the whole content → fill the card edge-to-edge (title goes in the footer). */
  imageFull?: boolean;
  pill?: PillState;
  ratio?: string;
}) {
  const t = aha ? AHA_THEME : stepTheme(type) ?? THEME.mixed;
  const rgb = ACCENT_RGB[t.accent];
  const deep = ACCENT_DEEP[t.accent];
  const hasImage = !!image;

  // ── full-bleed image: the image fills the card and stands clean — no dark
  //    mask. The title sits in the footer below (like video tiles), so nothing
  //    is laid over the picture. ──
  if (hasImage && imageFull) {
    return (
      <div style={{ position: 'relative', aspectRatio: ratio, overflow: 'hidden', background: '#fff' }}>
        <img src={image} alt="" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
        <div style={{ position: 'absolute', top: 12, right: 12 }}>
          <FavouriteButton stepId={stepId} />
        </div>
      </div>
    );
  }

  // ── otherwise a panel: white when there's an illustration, tinted when not ──
  return (
    <div style={{ position: 'relative', aspectRatio: ratio, overflow: 'hidden', background: hasImage ? '#fffdf8' : FIELD[t.accent] }}>
      {!hasImage && <Motif kind={t.motif} rgb={rgb} />}
      {!hasImage && (
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(90% 70% at 50% -16%, rgba(255,252,245,0.9), transparent 60%)', pointerEvents: 'none' }} />
      )}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          <FavouriteButton stepId={stepId} />
        </div>

        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 9, pointerEvents: 'none' }}>
          {hasImage ? (
            <img src={image} alt="" aria-hidden="true" style={{ maxWidth: '64%', maxHeight: '70%', objectFit: 'contain' }} />
          ) : (
            <GlyphWell size={58} radius={17} tone={t.accent} color={deep}>
              <Icon name={t.glyph} size={26} />
            </GlyphWell>
          )}
          {!hasImage && (
            <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: deep }}>
              {t.label}
              {metaText ? ` · ${metaText}` : ''}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <StatePill state={pill} />
        </div>
      </div>
    </div>
  );
}
